import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Performance Service - Optimize Lighthouse scores and Core Web Vitals
 */
@Injectable({
  providedIn: 'root'
})
export class PerformanceService {

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.initializePerformanceMonitoring();
    this.implementPerformanceOptimizations();
  }

  /**
   * Initialize performance monitoring
   */
  private initializePerformanceMonitoring(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!window.performance || !window.performance.timing) {
      return;
    }

    // Log Core Web Vitals
    if ('web-vital' in window) {
      this.trackCoreWebVitals();
    }
  }

  /**
   * Track Core Web Vitals (LCP, FID, CLS)
   */
  private trackCoreWebVitals(): void {
    // Largest Contentful Paint
    const observer = new PerformanceObserver((list) => {
      // Metrics can be sent to an analytics service here
    });

    observer.observe({ entryTypes: ['largest-contentful-paint'] });

    // First Input Delay / Interaction to Next Paint
    const fiObserver = new PerformanceObserver((list) => {
      // Metrics can be sent to an analytics service here
    });

    fiObserver.observe({ entryTypes: ['first-input', 'next-paint'] });

    // Cumulative Layout Shift
    const clsObserver = new PerformanceObserver((list) => {
      // Metrics can be sent to an analytics service here
    });

    clsObserver.observe({ entryTypes: ['layout-shift'] });
  }

  /**
   * Monitor long tasks (>50ms)
   */
  private monitorLongTasks(): void {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        console.warn('Long task detected:', entry.duration, 'ms', entry.name);
      }
    });

    observer.observe({ entryTypes: ['longtask'] });
  }

  /**
   * Implement performance optimizations
   */
  private implementPerformanceOptimizations(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.enableResourceHints();
    this.optimizeImages();
    this.lazyLoadIframes();
  }

  /**
   * Enable resource hints (prefetch, preconnect, preload)
   */
  private enableResourceHints(): void {
    const head = document.head;

    // Preconnect to external domains
    const externalDomains = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://cdn.jsdelivr.net',
      'https://analytics.google.com'
    ];

    externalDomains.forEach(domain => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = domain;
      head.appendChild(link);
    });

    // DNS prefetch for analytics
    const dnsPrefetch = document.createElement('link');
    dnsPrefetch.rel = 'dns-prefetch';
    dnsPrefetch.href = 'https://analytics.google.com';
    head.appendChild(dnsPrefetch);
  }

  /**
   * Optimize images - lazy loading
   */
  private optimizeImages(): void {
    document.querySelectorAll('img[data-src]').forEach(img => {
      const imgElement = img as HTMLImageElement;

      // Use Intersection Observer for lazy loading
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const src = imgElement.getAttribute('data-src');
              if (src) {
                imgElement.src = src;
                imgElement.removeAttribute('data-src');
              }
              observer.unobserve(imgElement);
            }
          });
        });

        observer.observe(imgElement);
      } else {
        // Fallback for older browsers
        const src = imgElement.getAttribute('data-src');
        if (src) {
          imgElement.src = src;
        }
      }
    });

    // Native lazy loading
    document.querySelectorAll('img:not([loading])').forEach(img => {
      img.setAttribute('loading', 'lazy');
    });
  }

  /**
   * Defer non-critical CSS
   */
  private deferNonCriticalCSS(): void {
    // Intentionally left as a no-op.
    // If needed later, only defer explicitly marked non-critical stylesheets.
  }

  /**
   * Lazy load iframes
   */
  private lazyLoadIframes(): void {
    document.querySelectorAll('iframe[data-src]').forEach(iframe => {
      const iframeElement = iframe as HTMLIFrameElement;

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const src = iframeElement.getAttribute('data-src');
              if (src) {
                iframeElement.src = src;
              }
              observer.unobserve(iframeElement);
            }
          });
        });

        observer.observe(iframeElement);
      } else {
        const src = iframeElement.getAttribute('data-src');
        if (src) {
          iframeElement.src = src;
        }
      }
    });
  }

  /**
   * Minify and compress (handled by build tools, this is just documentation)
   */
  private minifyAndCompress(): void {
    // Ensure gzip compression is enabled on server
    // Configuration examples:
    // 1. nginx: gzip on; gzip_types text/plain text/css application/json application/javascript text/xml;
    // 2. Apache: mod_deflate module configured
    // 3. Node.js: compression middleware

    console.log('Compression should be configured on the server');
  }

  /**
   * Get performance metrics
   */
  public getPerformanceMetrics(): PerformanceMetrics {
    if (!window.performance || !window.performance.timing) {
      return {} as PerformanceMetrics;
    }

    const timing = performance.timing as any;
    const navigation = performance.navigation;

    return {
      // Navigation timing
      dnsLookup: (timing.domainLookupEnd || 0) - (timing.domainLookupStart || 0),
      tcpConnect: (timing.connectEnd || 0) - (timing.connectStart || 0),
      timeToFirstByte: (timing.responseStart || 0) - (timing.navigationStart || 0),
      pageLoadTime: (timing.loadEventEnd || 0) - (timing.navigationStart || 0),
      domInteractive: (timing.domInteractive || 0) - (timing.navigationStart || 0),
      domComplete: (timing.domComplete || 0) - (timing.navigationStart || 0),

      // Resource timing
      resourcesCount: performance.getEntriesByType('resource').length,
      transferSize: this.calculateTransferSize(),

      // Paint timing
      firstPaint: this.getFirstPaint(),
      firstContentfulPaint: this.getFirstContentfulPaint()
    };
  }

  /**
   * Calculate total transfer size
   */
  private calculateTransferSize(): number {
    return performance.getEntriesByType('resource').reduce((total, entry) => {
      return total + ((entry as PerformanceResourceTiming).transferSize || 0);
    }, 0);
  }

  /**
   * Get first paint timing
   */
  private getFirstPaint(): number {
    const paintEntries = performance.getEntriesByType('paint');
    const fp = paintEntries.find(entry => entry.name === 'first-paint');
    return fp ? fp.startTime : 0;
  }

  /**
   * Get first contentful paint timing
   */
  private getFirstContentfulPaint(): number {
    const paintEntries = performance.getEntriesByType('paint');
    const fcp = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    return fcp ? fcp.startTime : 0;
  }

  /**
   * Get Lighthouse score prediction
   */
  public getLighthouseScorePrediction(): LighthouseScore {
    const metrics = this.getPerformanceMetrics();

    return {
      performance: this.calculatePerformanceScore(metrics),
      accessibility: 95, // Assuming good a11y practices
      bestPractices: 90,
      seo: 95,
      pwa: 90
    };
  }

  /**
   * Calculate performance score based on metrics
   */
  private calculatePerformanceScore(metrics: PerformanceMetrics): number {
    let score = 100;

    // Deduct points based on metrics
    if (metrics.pageLoadTime > 3000) score -= 20;
    else if (metrics.pageLoadTime > 2000) score -= 10;

    if (metrics.firstContentfulPaint > 1800) score -= 15;
    else if (metrics.firstContentfulPaint > 1000) score -= 5;

    if (metrics.transferSize > 5000000) score -= 10; // 5MB
    else if (metrics.transferSize > 3000000) score -= 5; // 3MB

    return Math.max(0, Math.min(100, score));
  }
}

/**
 * Performance Metrics Interface
 */
export interface PerformanceMetrics {
  dnsLookup: number;
  tcpConnect: number;
  timeToFirstByte: number;
  pageLoadTime: number;
  domInteractive: number;
  domComplete: number;
  resourcesCount: number;
  transferSize: number;
  firstPaint: number;
  firstContentfulPaint: number;
}

/**
 * Lighthouse Score Interface
 */
export interface LighthouseScore {
  performance: number;
  accessibility: number;
  bestPractices: number;
  seo: number;
  pwa: number;
}
